import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { Fullscreen, FullscreenExit } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { scroller } from 'react-scroll';

import styled, { css } from '../../styled';
import { colorsPalette } from '../../theme';
import { isIOSDevice, isScreenRotated } from '../../utils/helpers';
import { useEventListener } from '../../utils/hooks';
import useVerse, { VerseEvents } from '../../utils/hooks/useVerse';
import AnimatedSkeleton from '../AnimatedSkeleton';

interface VideoPlayerProps {
  id?: string;
  src?: string;
  className?: string;
  duration?: number;
  title?: string;
  expertName?: string;
  verseId?: string | null;
  videoUrl?: string | null;
  bumperVideoUrl?: string | null;
  onToggleFullscreen?: (isFullscreen: boolean) => void;
  isFullScreen?: boolean;
  additionalIframeUrlParams?: string;
  children?: ({ setShowBumper }: { setShowBumper: (isOpen: boolean) => void }) => ReactNode;
  waitForVideoLoading?: boolean;
}

const exitFullscreen = () => {
  if (document.fullscreenElement) {
    document.exitFullscreen();
  }
};

const VideoPlayer = React.forwardRef<HTMLFrameElement, VideoPlayerProps>(
  (
    {
      verseId,
      className = '',
      duration = null,
      expertName,
      title,
      id,
      videoUrl,
      bumperVideoUrl,
      onToggleFullscreen,
      isFullScreen,
      additionalIframeUrlParams = '',
      children,
      waitForVideoLoading = false,
    },
    ref,
  ) => {
    const isIOS = isIOSDevice();
    const [isIosFullScreen, setIsIosFullScreen] = useState<boolean>(false);
    const [isRotated, setIsRotated] = useState<boolean>(false);
    const isRotatedIOS = isIOS && isRotated;
    const [isVideoLoaded, setIsVideoLoaded] = useState(false);

    const [showBumper, setShowBumper] = useState(false);

    const iframeRef = useRef<HTMLIFrameElement>(null);
    // @ts-ignore
    const contentWindow = ref?.current?.contentWindow || iframeRef?.current?.contentWindow;
    const { changeVideoDuration } = useVerse(contentWindow);

    const url = verseId
      ? `${process.env.REACT_APP_VERSE_PLAYER_URL}/stories/${verseId}-xunlocked/?embed&controls=2&sharing=0&covertitle=0${additionalIframeUrlParams}`
      : videoUrl || '';

    useEffect(() => {
      setIsRotated(isScreenRotated());
    }, []);

    //used to save fullscreen state on screen rotate that cause modal remount
    useEffect(() => {
      if (typeof isFullScreen === 'boolean') {
        setIsIosFullScreen(isFullScreen);
      }
    }, [isFullScreen]);

    const exitIosFullscreen = useCallback(() => {
      setIsIosFullScreen(false);
      onToggleFullscreen?.(false);
      if (window?.userengage) {
        window.userengage('widget.show');
      }
      //scroll to video while exiting from fullscreen mode
      if (isIosFullScreen) {
        scroller.scrollTo('playerWrap', {
          duration: 1000,
          smooth: 'easeInOutQuart',
          offset: -80,
        });
      }
    }, [isIosFullScreen, onToggleFullscreen]);

    const toggleIosFullScreen = useCallback(
      (directMode?: boolean) => {
        if (directMode === false || (directMode === undefined && isIosFullScreen)) {
          exitIosFullscreen();
        } else if ((directMode === undefined && !isIosFullScreen) || directMode === true) {
          setIsIosFullScreen(true);
          onToggleFullscreen?.(true);
          if (window?.userengage) {
            window.userengage('widget.hide');
          }
        }
      },
      [isIosFullScreen, exitIosFullscreen, onToggleFullscreen],
    );

    const handleVideoWatched = useCallback(() => {
      if (bumperVideoUrl) {
        setShowBumper(true);
      }

      if (isIosFullScreen && exitIosFullscreen) {
        exitIosFullscreen();
      } else {
        exitFullscreen();
      }
    }, [bumperVideoUrl, exitIosFullscreen, isIosFullScreen]);

    const handleResizeIosFullscreen = useCallback(() => {
      if (isIOSDevice()) {
        const isRotated = isScreenRotated();
        setIsRotated(isRotated);
        toggleIosFullScreen(isRotated);
      }
    }, [toggleIosFullScreen]);

    useEventListener('orientationchange', handleResizeIosFullscreen);
    useEventListener(VerseEvents.LastChapterFinished, handleVideoWatched);

    useEffect(() => {
      if (duration) {
        changeVideoDuration(duration, title, expertName);
      }
    }, [title, expertName, duration, changeVideoDuration]);

    return (
      <Wrapper optionalStyles={isIosFullScreen && generatedFullScreen}>
        {waitForVideoLoading && !isVideoLoaded && <LoadingOverlay />}
        {isIosFullScreen ? (
          <FullscreenExitIcon
            onClick={(e) => {
              e.stopPropagation();
              toggleIosFullScreen();
            }}
            isRotatedIOS={isRotatedIOS}
          />
        ) : (
          <FullScreenIcon
            onClick={(e) => {
              e.stopPropagation();
              toggleIosFullScreen();
            }}
            isRotatedIOS={isRotatedIOS}
          />
        )}
        {bumperVideoUrl && (
          <Bumper data-testid="bumperWrapper" optionalStyles={showBumper && generatedActive}>
            <Video autoPlay muted playsInline>
              <source src={bumperVideoUrl} />
            </Video>
          </Bumper>
        )}
        {children?.({ setShowBumper })}
        {/*eslint-disable-next-line*/}
        <iframe
          data-testid="videoPlayer"
          id={id}
          className={className}
          src={url}
          frameBorder="0"
          allow="autoplay"
          allowFullScreen
          // @ts-ignore
          ref={ref || iframeRef}
          onLoad={() => setIsVideoLoaded(true)}
        />
      </Wrapper>
    );
  },
);

export default VideoPlayer;

const LoadingOverlay = styled(AnimatedSkeleton)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Wrapper = styled('div')`
  position: relative;
  width: 100%;
  height: 100%;
`;
const generatedFullScreen = css`
  border-radius: 0;
  width: 100vw;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1200;
`;
const createFullScreenIcon = (tag) => styled(tag, {
  shouldForwardProp: (prop) => prop !== 'isRotatedIOS',
})`
  position: absolute;
  top: 10px;
  left: 10px;
  color: ${alpha(colorsPalette.white, 0.7)};
  z-index: 1300;
  display: ${({ isRotatedIOS }) => (!isRotatedIOS ? 'none' : 'unset')};

  &:hover {
    color: ${colorsPalette.white};
  }
`;
const FullscreenExitIcon = createFullScreenIcon(FullscreenExit);
const FullScreenIcon = createFullScreenIcon(Fullscreen);
const Bumper = styled('div', {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  background: ${colorsPalette.blackPure};
  display: none;

  ${({ optionalStyles }) => optionalStyles}
`;
const generatedActive = css`
  display: block;
`;
const Video = styled('video')`
  width: 100%;
  height: 100%;
`;
